import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import {
  AssignmentCustomerResponse,
  BASE_PERMISSIONS,
  baseAssignmentsActions,
  BaseAvatarSize,
  BaseButtonSize,
  BaseContractSigningTypeEnum,
  BaseCoreState,
  BaseOrderDirectionEnum,
  BaseOrderKeyEnum,
  BaseRolesService,
  BaseSignatureService,
  BaseSignContractOptions,
  CustomerAssignmentListItemResponse,
  BaseIntervalStatusType,
  BaseIntervalStatus,
  BaseDateService,
  BaseSigningStatus,
  BaseEmployeeOperationService,
} from 'base';
import { BaseCustomerEmployeesFiltersEnum } from '../../_enums/base-customer-external-employee-filters.enum';
import { ASSIGNMENT_MENU } from '../../../../shared/_constants/assignment-menu';
import { ExternalEmployeeService } from '../../external-employee.service';
import { BaseCustomerOfferTabsEnum } from '../../../inquiry/_enums/base-customer-offer-tabs.enum';
import { AssignmentListItem } from '../../_interfaces/base-customer-assignment.interface';

@Component({
  selector: 'base-customer-external-employees-list-item',
  templateUrl: './base-customer-external-employee-list-item.component.html',
})
export class BaseCustomerExternalEmployeeListItemComponent implements OnInit, OnDestroy {
  @Input() assignmentListItem: AssignmentListItem | undefined;
  @Input() listOrderKey: BaseOrderKeyEnum;
  @Input() listOrderDirection: BaseOrderDirectionEnum;
  @Input() filters: { [key in BaseCustomerEmployeesFiltersEnum]: any };
  @Input() page: number;
  @Input() pageSize: number;
  @Input({ required: true }) isWorkCouncil: boolean;
  @Output() selectItem: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('employeeInfoModalBody') employeeInfoModalBody: ElementRef;
  @ViewChild('addSignSelectionPopup') addSignSelectionPopup: ElementRef;
  @ViewChild('addSignPopup') addSignPopup: ElementRef;

  customerAssignment: AssignmentCustomerResponse;
  modalRef: NgbModalRef;
  buttonSizes = BaseButtonSize;
  avatarSize = BaseAvatarSize;
  profileFullName: string;
  statusButtonBorder: string;
  destroy$ = new Subject<void>();
  hasSignContractPermission = false;
  hasWorkCouncilRole = false;
  tabs = [...ASSIGNMENT_MENU];
  currentTab = this.tabs[BaseCustomerOfferTabsEnum.MaterData];
  signingStatusBySide = BaseSigningStatus;
  contractSigningType = BaseContractSigningTypeEnum;
  assignmentStatus: BaseIntervalStatusType;
  signingType: BaseSignContractOptions | undefined;
  digitalSignModalRef: NgbModalRef;
  addSignModalRef: NgbModalRef;
  intervalStatus = BaseIntervalStatus;
  agreedWeeklyHours: string | null = null;

  constructor(
    private store: Store<BaseCoreState>,
    private baseRolesService: BaseRolesService,
    private router: Router,
    public externalEmployeeService: ExternalEmployeeService,
    public baseSignatureService: BaseSignatureService,
    private employeeOperationService: BaseEmployeeOperationService
  ) {}

  ngOnInit() {
    this.hasSignContractPermission = this.baseRolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_SIGN_CONTRACT
    );
    this.hasWorkCouncilRole = this.baseRolesService.hasWorkCouncilRole();

    if (this.assignmentListItem) {
      this.profileFullName = `${this.assignmentListItem.profile.firstName} ${this.assignmentListItem.profile.lastName}`;
      this.updateStatus(this.assignmentListItem);
      this.setAgreedWeeklyHours(this.assignmentListItem);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateStatus(assignment: CustomerAssignmentListItemResponse & { isSelected: boolean }) {
    this.assignmentStatus = BaseDateService.getIntervalStatus(assignment.startDate, assignment.endDate);

    if (this.assignmentListItem && !!assignment.contract) {
      this.statusButtonBorder = this.externalEmployeeService.setSignButtonClass(
        this.assignmentListItem.contract,
        this.hasSignContractPermission
      );
    }
  }

  onItemSelect(event: Event): void {
    if (!this.assignmentListItem) {
      return;
    }

    event.stopPropagation();
    this.selectItem.emit(this.assignmentListItem.id);
  }

  openTimeRecording(event: Event) {
    event.stopPropagation();
    this.currentTab = this.tabs.find((tab) => tab.id === BaseCustomerOfferTabsEnum.TimeRecordings)!;
    this.openEmployeeInfoModal();
  }

  openSignContractsModal(event: Event) {
    if (!this.assignmentListItem) {
      return;
    }

    event.stopPropagation();

    const canBeSigned = this.externalEmployeeService.canBeSigned(
      this.hasSignContractPermission,
      this.assignmentListItem.contract
    );

    if (!canBeSigned) {
      return;
    }

    this.store.dispatch(baseAssignmentsActions.clearContractStatus());
    this.getAssignment(this.assignmentListItem).subscribe((assignment) => {
      this.customerAssignment = assignment;

      this.addSignModalRef = this.baseSignatureService.openSignContractModal(this.addSignSelectionPopup);
      this.addSignModalRef.result.then(
        () => {
          if (this.signingType === BaseSignContractOptions.signDigital) {
            this.openDigitalSignModal();
          } else {
            if (this.assignmentListItem) {
              this.baseSignatureService.dispatchAssignContractAction(
                this.assignmentListItem.id,
                this.assignmentListItem.contract.id,
                this.signingType,
                false
              );
            }
          }
        },
        () => {
          this.signingType = undefined;
        }
      );
    });
  }

  openDigitalSignModal() {
    this.digitalSignModalRef = this.externalEmployeeService.openDigitalSignModal(this.addSignPopup);
    this.digitalSignModalRef.result.then(() => noop, noop);
  }

  openEmployeeInfoModal(): void {
    if (!this.assignmentListItem || this.isWorkCouncil) {
      return;
    }

    this.getAssignment(this.assignmentListItem).subscribe((assignment) => {
      this.customerAssignment = assignment;
      this.showAssignmentModal();
    });
  }

  getAssignment(assignment: AssignmentListItem) {
    return this.externalEmployeeService.getAssignment(assignment.id);
  }

  showAssignmentModal() {
    this.modalRef = this.externalEmployeeService.showAssignmentModal(this.employeeInfoModalBody);
    this.modalRef.result.then(noop, noop);
  }

  onSignTypeChange(event: BaseSignContractOptions): void {
    this.signingType = event;
  }

  openIzsInfoModal(event: Event): void {
    if (!this.assignmentListItem) {
      return;
    }

    event.stopPropagation();
    this.router.navigate(['/service-provider'], {
      queryParams: {
        page: 1,
        search: this.assignmentListItem.pspBranch.name,
        pspBranchId: this.assignmentListItem.pspBranch.id,
        location: this.assignmentListItem.customerLocation.id,
      },
    });
  }

  private setAgreedWeeklyHours(assignment: AssignmentListItem): void {
    const weeklyHours = Number(assignment.weeklyWorkingHours);

    this.agreedWeeklyHours = weeklyHours ? this.employeeOperationService.formatAgreedHours(weeklyHours) : null;
  }
}
